// Lead-type metadata — single source of truth for the "why is this a lead"
// label shown when a property is a pre-auction LEAD (Property.inAuction ===
// false) rather than a live auction. Mirrors the statusMeta.ts pattern: one
// registry, one fallback, callers never null-check.
//
// Design-system note (docs/design/DESIGN_SYSTEM.md §2-3): "the only other
// colors allowed [besides the one blue accent] are the status colors ... and
// depicted data." Lead types are a second semantic dimension layered on top
// of status, not a new decoration, so this registry deliberately does NOT
// mint new hex values — every color below is imported straight from
// STATUS_META (lib/statusMeta.ts) and reused for its closest semantic match
// (e.g. tax_foreclosure reuses the existing "in_rem" tax-foreclosure cyan).
// If you need a new lead-type color, add it to STATUS_META first (with the
// matching index.css --status-* var per DESIGN_SYSTEM.md §3), then point
// here — never inline a hex in this file.

import { LeadSignal } from "../types";
import { STATUS_META } from "./statusMeta";

export type LeadSignalType = LeadSignal["signalType"];

export interface LeadTypeInfo {
  /** Signal-type key, e.g. "tax_foreclosure" */
  key: LeadSignalType;
  /** Human label for the card pill / detail badge, e.g. "Tax Foreclosure" */
  label: string;
  /** Hex color, reused from STATUS_META — never a new literal (see file header). */
  color: string;
}

/**
 * Priority order (highest first). When a parcel carries multiple
 * leadSignals, the headline pill shows the highest-priority one; the rest
 * surface as a "+N" indicator. Government/legal-proceeding signals against
 * the property itself (tax, mortgage, generic foreclosure, HOA) outrank
 * signals that merely correlate with a likely future sale (probate,
 * divorce).
 */
export const LEAD_SIGNAL_PRIORITY: LeadSignalType[] = [
  // condemnation outranks every foreclosure signal. It is not a prediction
  // that the property MIGHT sell — it is a recorded fact about the structure:
  // under NC G.S. 160D-1203 an order to repair, close or demolish is filed
  // with the Register of Deeds, indexed against the owner, and the demolition
  // cost becomes a lien. It also changes what the property IS, which a
  // foreclosure signal does not.
  "condemnation",
  "tax_foreclosure",
  "mortgage_foreclosure",
  "foreclosure",
  "hoa_foreclosure",
  // execution_sale sits with the other junior-position forced sales: it is a
  // filed proceeding with a noticed sale date (so it outranks probate/divorce,
  // which merely correlate with a future sale), but a sheriff's levy satisfies
  // a money judgment and generally stands behind a first lien, so it does not
  // outrank a deed-of-trust or tax foreclosure. See leadsignals.go's
  // SignalExecutionSale doc comment (#936).
  "execution_sale",
  // trustee_substitution is an early warning, not a proceeding: a lender appointed
  // a new trustee and about half reach a court case (ADR 0097). It outranks probate
  // and divorce because it is about this loan, and sits below everything filed.
  "trustee_substitution",
  "probate",
  "divorce",
  // code_enforcement sits below the filed proceedings and above
  // tax_delinquent: an open housing/nuisance case is real, adjudicated
  // evidence of physical distress, but no legal process against the title has
  // begun. See leadsignals.go SignalCodeEnforcement — "adjacent evidence, not
  // a standalone lead reason".
  "code_enforcement",
  // tax_delinquent is deliberately LAST — it is months-to-years earlier and
  // far noisier than every signal above (NCGS 105-374/375 escalation from
  // delinquency to an actual foreclosure filing is fully discretionary, most
  // delinquencies never get there). It should never win the headline pill
  // over a real filed proceeding on the same parcel. See leadsignals.go's
  // SignalTaxDelinquent doc comment (#693).
  "tax_delinquent",
];

export const LEAD_TYPE_META: Record<LeadSignalType, LeadTypeInfo> = {
  tax_foreclosure: {
    key: "tax_foreclosure",
    label: "Tax Foreclosure",
    color: STATUS_META.in_rem.color, // reuses the existing in-rem tax-foreclosure cyan
  },
  mortgage_foreclosure: {
    key: "mortgage_foreclosure",
    label: "Mortgage Foreclosure",
    color: STATUS_META.notice_of_default.color, // reuses the pre-foreclosure/NOD blue
  },
  foreclosure: {
    key: "foreclosure",
    label: "Foreclosure",
    color: STATUS_META.notice_filed.color, // reuses the generic notice-filed amber
  },
  hoa_foreclosure: {
    key: "hoa_foreclosure",
    label: "HOA Foreclosure",
    color: STATUS_META.res_plus.color, // reuses the residential-plus purple
  },
  probate: {
    key: "probate",
    label: "Probate",
    color: STATUS_META.delisted.color, // reuses the neutral gray
  },
  divorce: {
    key: "divorce",
    label: "Divorce",
    color: STATUS_META.auction_passed.color, // reuses the dark slate
  },
  execution_sale: {
    key: "execution_sale",
    label: "Execution Sale",
    // reuses the auction_scheduled color: a 29B sale IS a scheduled sheriff's
    // sale with a noticed date and a 10-day upset window (G.S. 1-339.64), so
    // this is the closest semantic match in the existing palette. Per the file
    // header, no new hex.
    color: STATUS_META.auction_scheduled.color,
  },
  trustee_substitution: {
    key: "trustee_substitution",
    // Never "Pre-Foreclosure": that label means notice_of_default in statusMeta.ts,
    // and no foreclosure has been filed on these.
    label: "Trustee Change",
    color: STATUS_META.postponed.color, // reuses an existing status color, no new hex
  },
  condemnation: {
    key: "condemnation",
    label: "Condemned",
    // reuses the terminal "sold" color — the strongest, most final-reading
    // hue in the palette, matching a structure the city has adjudicated
    // unfit. Nothing else in the registry claims it.
    color: STATUS_META.sold.color,
  },
  code_enforcement: {
    key: "code_enforcement",
    label: "Code Violation",
    // reuses the postponed amber/orange — an active, unresolved condition
    // that has not yet reached a terminal outcome, which is exactly what an
    // open code-enforcement case is.
    color: STATUS_META.postponed.color,
  },
  tax_delinquent: {
    key: "tax_delinquent",
    label: "Tax Delinquent",
    // reuses the muted "withdrawn" slate — no unused color is a perfect
    // semantic match (in_rem's cyan is closest but already claimed by
    // tax_foreclosure), so this deliberately picks the most desaturated/
    // muted option in the palette to visually read as "weakest, earliest,
    // unconfirmed signal" rather than an active proceeding.
    color: STATUS_META.withdrawn.color,
  },
};

const UNKNOWN_LEAD_TYPE: LeadTypeInfo = {
  key: "foreclosure",
  label: "Lead",
  color: STATUS_META.notice_filed.color,
};

// Signals we hide from user-facing filter surfaces because the underlying data
// is too thin to sell against — the *signal* still exists in the priority list
// and in leadTypeMeta() (so a label resolves if one slips through), but the
// filter dropdowns/buy-box chips omit it.
// - divorce: 552 notices, 1 (0.2%) matched (2026-09-16). Zero property-attached
//   signals.
// - probate was hidden here until 2026-10-23 (23 of 2,338 notices matched a
//   property on 2026-09-16). Un-hidden on owner direction: 237 property-attached
//   probate signals across 53 counties now (1 in Mecklenburg), and executor
//   name/address capture plus Mecklenburg estate discovery are being built, so
//   coverage will rise. Expect a short list in most counties until then.
// Re-check the counts before promoting divorce back into the visible set.
const HIDDEN_LEAD_SIGNALS: ReadonlySet<LeadSignalType> = new Set<LeadSignalType>([
  "divorce",
]);

/**
 * Priority-ordered subset of LEAD_SIGNAL_PRIORITY that filter dropdowns and
 * buy-box chip rows iterate. Anything in HIDDEN_LEAD_SIGNALS is excluded — see
 * that constant for the coverage numbers driving each exclusion.
 */
export const USER_VISIBLE_LEAD_SIGNALS: LeadSignalType[] = LEAD_SIGNAL_PRIORITY.filter(
  (t) => !HIDDEN_LEAD_SIGNALS.has(t),
);

/**
 * Derives which signal types actually have presence in a given set of
 * property/lead rows and returns the priority-ordered, user-visible subset
 * (see USER_VISIBLE_LEAD_SIGNALS) — the shape filter dropdowns should render
 * as their <option> list for the currently-selected county (#2257).
 *
 * Deliberately reuses whatever rows the caller already has in memory (the
 * county's `properties` from useCountyProperties for SearchAutocomplete, the
 * county's `leads` for BuyBoxBar) rather than adding a new endpoint — see the
 * issue's own scope note. Each row can supply either a `leadSignals` array
 * (Property — objects with a `signalType` field) or a flat `signalTypes`
 * array (Lead/BuyBox — already strings); accepting both lets every call site
 * pass its native shape without a mapping step.
 *
 * Presence-only (count > 0), not a percentage threshold: unlike the
 * probate/divorce HIDDEN_LEAD_SIGNALS cut (a permanent, measured-against-prod
 * call), this runs per county per render against whatever the county's own
 * data actually contains, so "near-zero" degenerates to "zero" here — there
 * is no stable statewide baseline to compare a single county's count against.
 *
 * Returns USER_VISIBLE_LEAD_SIGNALS unfiltered (i.e. every option) when
 * `rows` is empty — an empty array means "no data yet" (still loading, or a
 * genuinely brand-new county) far more often than "this county has zero of
 * every signal type," and hiding every option while loading would read as a
 * broken dropdown rather than a scoped one.
 */
export function computeAvailableSignalTypes(
  rows: ReadonlyArray<{ leadSignals?: LeadSignal[]; signalTypes?: string[] }>,
): LeadSignalType[] {
  if (rows.length === 0) return USER_VISIBLE_LEAD_SIGNALS;
  const present = new Set<string>();
  for (const row of rows) {
    if (row.leadSignals) {
      for (const s of row.leadSignals) present.add(s.signalType);
    }
    if (row.signalTypes) {
      for (const t of row.signalTypes) present.add(t);
    }
  }
  return USER_VISIBLE_LEAD_SIGNALS.filter((t) => present.has(t));
}

/**
 * Look up display info for a lead-signal key. Always returns a LeadTypeInfo
 * (never null); an unrecognized key gets a generic "Lead" fallback so a new
 * backend signalType never renders a blank pill.
 */
export function leadTypeMeta(key: string | null | undefined): LeadTypeInfo {
  if (!key) return UNKNOWN_LEAD_TYPE;
  return (LEAD_TYPE_META as Record<string, LeadTypeInfo>)[key] ?? UNKNOWN_LEAD_TYPE;
}

/**
 * Picks the single highest-priority signal from a property's leadSignals
 * (per LEAD_SIGNAL_PRIORITY) for the headline pill. Returns null when the
 * array is empty or missing so callers can fall back to the status label.
 */
export function primaryLeadSignal(signals: LeadSignal[] | undefined | null): LeadSignal | null {
  if (!signals || signals.length === 0) return null;
  let best: LeadSignal | null = null;
  let bestRank = Infinity;
  for (const s of signals) {
    const rank = LEAD_SIGNAL_PRIORITY.indexOf(s.signalType);
    const effectiveRank = rank === -1 ? LEAD_SIGNAL_PRIORITY.length : rank;
    if (effectiveRank < bestRank) {
      bestRank = effectiveRank;
      best = s;
    }
  }
  return best;
}
